// Nomad-mon Go! — FRONT DOOR medallion.
// One deterministic Three.js group owns arrival, idle and exit. The shared
// renderer/camera remain in main.js; this module only authors subject motion.

import * as THREE from 'three';
import { makeLightning, boltLines, makePlasma, makeSparks, makeCaustic, makeLensPass } from './entry-spell.js';

const ARRIVAL_END = 0.78;
// FRONT DOOR 6 "spell": the handoff is a choreographed five-beat sequence
// (Jack: "even more spectacular", earlier "goes by way too quickly").
//   PRESS   0-0.34   the tap presses the medal in; three bolts strike the rim
//                    (restrikes at +70/+150ms), shockwave refraction, sparks
//   CHARGE  0.24-1.55 it lifts and turns once while arcs crawl the relief,
//                    the rim ignites in energy fire with heat shimmer and the
//                    prism fan sweeps around it
//   CLIMAX  1.55     outward discharge: six bolts, chromatic shock ring, spark ring
//   PLUNGE  1.70-3.0 it dives through the lens into a spectral zoom and bloom
//                    flash; the destination is revealed at EXIT_REVEAL
// A tap during the sequence jumps straight to EXIT_REVEAL (skipExit).
// Reduced motion gets a calm 0.9s cross-fade (reveal 0.45s), no effects.
const EXIT_END = 3.0;
export const EXIT_REVEAL = 2.15;
const THUD_END = 0.34, GATHER_START = 0.24, CLIMAX = 1.55, PLUNGE_START = 1.70;
const CALM_EXIT_END = 0.9, CALM_REVEAL = 0.45;
// audio beats (seconds after the tap) for main.js to schedule the cues on
export const ENTRY_BEATS = Object.freeze({ press: 0, climax: CLIMAX, plunge: PLUNGE_START, reveal: EXIT_REVEAL });
const STRIKE_SITES = [1.83, 0.62, 2.79];        // rim angles the three press bolts hit
const MEDAL_RADIUS = 0.81;
const MEDAL_DIAMETER = MEDAL_RADIUS * 2;
const MEDAL_HALF_DEPTH = 0.052;
const THICKNESS_RATIO = (MEDAL_HALF_DEPTH * 2) / MEDAL_DIAMETER;
const ARRIVAL_PITCH = THREE.MathUtils.degToRad(2);
const CREST_CROP_RATIO = 0.59;
const clamp01 = (v) => Math.max(0, Math.min(1, v));
const smooth = (v) => { const x = clamp01(v); return x * x * (3 - 2 * x); };
const outQuint = (v) => 1 - Math.pow(1 - clamp01(v), 5);

function noiseYawDeg(time) {
  return 18 * Math.sin(time * Math.PI * 2 / 6.4)
    + 6 * Math.sin(time * Math.PI * 2 / 10.7)
    + 4 * Math.sin(time * Math.PI * 2 / 12.9);
}

// Ease to the rear, dwell long enough for the sculpted back to read, then
// finish the one-turn flip. The dwell also lets the underlying noise retain
// its signed velocity instead of becoming a long constant-direction spin.
function flipProgress(progress) {
  const p = clamp01(progress);
  if (p < 0.44) return 0.5 * smooth(p / 0.44);
  if (p < 0.56) return 0.5;
  return 0.5 + 0.5 * smooth((p - 0.56) / 0.44);
}

function mulberry32(a) {
  return function () {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function medalBodyGeometry(segments) {
  const p = [
    [0.742, -MEDAL_HALF_DEPTH], [0.782, -MEDAL_HALF_DEPTH * 0.94],
    [0.806, -MEDAL_HALF_DEPTH * 0.62], [MEDAL_RADIUS, -MEDAL_HALF_DEPTH * 0.22],
    [MEDAL_RADIUS, MEDAL_HALF_DEPTH * 0.22], [0.806, MEDAL_HALF_DEPTH * 0.62],
    [0.782, MEDAL_HALF_DEPTH * 0.94], [0.742, MEDAL_HALF_DEPTH],
  ].map(([x, y]) => new THREE.Vector2(x, y));
  const g = new THREE.LatheGeometry(p, segments);
  g.rotateX(Math.PI / 2);
  g.computeVertexNormals();
  return g;
}

function makeStage() {
  const uniforms = { uTime: { value: 0 }, uFade: { value: 1 }, uAurora: { value: 1 } };
  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), new THREE.ShaderMaterial({
    uniforms, depthTest: false, depthWrite: false,
    vertexShader: `varying vec2 vUv; void main(){ vUv=uv; gl_Position=vec4(position.xy,0.9999,1.0); }`,
    fragmentShader: /* glsl */`
      precision highp float;
      varying vec2 vUv;
      uniform float uTime, uFade, uAurora;
      float hash(vec2 p){ p=fract(p*vec2(123.34,456.21)); p+=dot(p,p+45.32); return fract(p.x*p.y); }
      float noise(vec2 p){ vec2 i=floor(p),f=fract(p); f=f*f*(3.0-2.0*f);
        return mix(mix(hash(i),hash(i+vec2(1,0)),f.x),mix(hash(i+vec2(0,1)),hash(i+vec2(1,1)),f.x),f.y); }
      void main(){
        vec2 p=(vUv-.5)*2.0; p.x*=1.18;
        float d=length(p);
        float warp=noise(p*1.7+vec2(uTime*.014,-uTime*.009));
        float ribbon=pow(max(0.0,.5+.5*sin(p.x*3.2+p.y*1.45+warp*4.0+uTime*.045)),5.0);
        float veil=smoothstep(1.25,.08,d)*ribbon*uAurora;
        vec3 col=vec3(.0014,.0012,.0052);
        col+=mix(vec3(.0060,.0028,.0200),vec3(.0022,.0110,.0180),warp)*veil;
        col+=vec3(.0070,.0030,.0130)*smoothstep(.62,.0,length(p-vec2(.12,.34)))*uAurora;
        col*=.42+.58*smoothstep(1.28,.22,d);
        gl_FragColor=vec4(col,1.0);
      }
    `,
  }));
  mesh.renderOrder = 1000;
  mesh.frustumCulled = false;
  return { mesh, uniforms };
}

function makeShockwave() {
  const uniforms = { uT: { value: 2 }, uFade: { value: 1 } };
  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(3.7, 3.7), new THREE.ShaderMaterial({
    uniforms, transparent: true, depthWrite: false, depthTest: false, blending: THREE.AdditiveBlending,
    vertexShader: `varying vec2 vUv; void main(){ vUv=uv; gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.0); }`,
    fragmentShader: /* glsl */`
      precision highp float; varying vec2 vUv; uniform float uT,uFade;
      void main(){ if(uT>=1.0) discard; float d=length((vUv-.5)*2.0);
        // FRONT DOOR 6: born at the rim (0.44 = r 0.81 in medal units) and
        // drawn over the metal, so it can no longer cut a half-moon in the face
        float r=.46+uT*.54; float w=30.0-uT*12.0;
        vec3 ring=vec3(exp(-pow((d-r*1.012)*w,2.0)),exp(-pow((d-r)*w,2.0)),exp(-pow((d-r*.988)*w,2.0)));
        float k=pow(1.0-uT,2.2)*uFade; vec3 c=ring*vec3(0.95,0.95,1.25)*k*0.75;
        gl_FragColor=vec4(c*2.2,max(max(c.r,c.g),c.b)); }
    `,
  }));
  mesh.position.z = 0.08;
  mesh.visible = false;
  return { mesh, uniforms };
}

function makeParticles(rng, count) {
  const positions = new Float32Array(count * 3);
  const attrs = new Float32Array(count * 4);
  for (let i = 0; i < count; i++) {
    const a = rng() * Math.PI * 2;
    const r = 0.96 + rng() * 1.15;
    positions[i * 3] = Math.cos(a) * r;
    positions[i * 3 + 1] = Math.sin(a) * r;
    positions[i * 3 + 2] = -0.15 + rng() * 0.55;
    attrs[i * 4] = rng();
    attrs[i * 4 + 1] = 0.08 + rng() * 0.18;
    attrs[i * 4 + 2] = 1.3 + rng() * 3.3;
    attrs[i * 4 + 3] = rng() < 0.28 ? 1 : 0;
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geo.setAttribute('aData', new THREE.BufferAttribute(attrs, 4));
  const uniforms = {
    uTime: { value: 0 }, uBurst: { value: 0 }, uFade: { value: 1 },
    uPointer: { value: new THREE.Vector2() },
  };
  const mat = new THREE.ShaderMaterial({
    uniforms, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending,
    vertexShader: /* glsl */`
      attribute vec4 aData; varying float vLife,vEmber,vHue;
      uniform float uTime,uBurst; uniform vec2 uPointer;
      void main(){ float life=fract(aData.x+uTime*aData.y); vec3 p=position;
        p.x+=sin(uTime*(.4+aData.y)+aData.x*31.0)*.12+uPointer.x*(.05+p.z*.04);
        p.y+=life*.42+cos(uTime*.31+aData.x*19.0)*.06-uPointer.y*(.04+p.z*.03);
        p.xy*=1.0+uBurst*(.45+aData.x*.75); vLife=sin(life*3.14159); vEmber=aData.w; vHue=aData.x;
        vec4 mv=modelViewMatrix*vec4(p,1.0); gl_PointSize=min(aData.z*(2.2/-mv.z)*3.0,10.0);
        gl_Position=projectionMatrix*mv; }
    `,
    fragmentShader: /* glsl */`
      precision highp float; varying float vLife,vEmber,vHue; uniform float uFade;
      void main(){ float d=smoothstep(.5,.05,length(gl_PointCoord-.5));
        vec3 c=mix(vec3(1.0,.93,.84),vec3(.80,.76,1.0),vHue*.6+vEmber*.4);
        float a=d*(.24+.76*vLife)*uFade; gl_FragColor=vec4(c*a*1.8,a); }
    `,
  });
  const points = new THREE.Points(geo, mat);
  points.frustumCulled = false;
  return { points, uniforms };
}

// V2.1: a tiny procedural PRISM studio prefiltered into a PMREM env map, so
// the medal is real chrome — a cool key softbox, cyan and magenta strip
// lights and a violet floor bounce slide across the relief as it turns.
// Built once at entry creation; violet-dominant so the face still reads violet.
function makePrismEnv(renderer) {
  if (!renderer) return null;
  try {
    const scene = new THREE.Scene();
    const mat = new THREE.ShaderMaterial({
      side: THREE.BackSide, depthWrite: false,
      vertexShader: `varying vec3 vDir; void main(){ vDir = normalize(position);
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
      fragmentShader: /* glsl */`
        precision highp float; varying vec3 vDir;
        float box(vec3 d, vec3 c, float k){ return pow(max(dot(normalize(d), normalize(c)), 0.0), k); }
        void main(){
          vec3 d = normalize(vDir);
          vec3 col = mix(vec3(0.016, 0.017, 0.026), vec3(0.075, 0.078, 0.105), smoothstep(-0.6, 0.9, d.y));
          col += vec3(1.05, 1.0, 1.25) * box(d, vec3(0.45, 0.75, 0.6), 22.0) * 3.2;     // key softbox
          col += vec3(0.70, 0.76, 1.00) * box(d, vec3(1.0, 0.05, 0.25), 9.0) * 1.1;     // cool strip
          col += vec3(1.00, 0.80, 0.62) * box(d, vec3(-0.9, -0.3, 0.45), 8.0) * 0.8;    // warm rim strip
          col += vec3(0.55, 0.57, 0.70) * box(d, vec3(0.0, -1.0, 0.2), 3.0) * 0.40;     // violet floor bounce
          col += vec3(1.1, 0.78, 0.5) * box(d, vec3(-0.4, 0.85, -0.3), 40.0) * 1.4;     // small warm kicker
          gl_FragColor = vec4(col, 1.0);
        }
      `,
    });
    scene.add(new THREE.Mesh(new THREE.SphereGeometry(5, 32, 16), mat));
    const pm = new THREE.PMREMGenerator(renderer);
    const rt = pm.fromScene(scene, 0.02);
    pm.dispose(); mat.dispose();
    return rt.texture;
  } catch { return null; }
}

// corona + light rays behind the medal (eclipse light): spectral shafts that
// slowly turn, a soft rim halo; uBoost floods it on the enter dive
function makeCorona() {
  const uniforms = { uTime: { value: 0 }, uFade: { value: 1 }, uIgnite: { value: 0 }, uBoost: { value: 0 },
    uPrism: { value: 0 }, uPrismAng: { value: 0.9 }, uHalo: { value: 0 },
    uDest: { value: new THREE.Vector3(1, 0.8, 0.55) }, uDestK: { value: 0 } };
  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(4.4, 4.4), new THREE.ShaderMaterial({
    uniforms, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending,
    vertexShader: `varying vec2 vUv; void main(){ vUv=uv; gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.0); }`,
    fragmentShader: /* glsl */`
      precision highp float; varying vec2 vUv;
      uniform float uTime, uFade, uIgnite, uBoost, uPrism, uPrismAng, uHalo, uDestK;
      uniform vec3 uDest;
      // smooth dispersion spectrum, violet (0) -> red (1); no hard bands
      vec3 spectral(float x){
        return clamp(vec3(exp(-pow((x - 0.82) / 0.2, 2.0)) + 0.3 * exp(-pow((x - 0.05) / 0.1, 2.0)),
                          exp(-pow((x - 0.5) / 0.2, 2.0)), exp(-pow((x - 0.16) / 0.2, 2.0))), 0.0, 1.0);
      }
      // light caught at a rim point E splits into a narrow spectral fan
      vec3 prismFan(vec2 p, float ang, float spread){
        vec2 nrm = vec2(cos(ang), sin(ang)); vec2 v = p - nrm * 0.83;
        float along = dot(v, nrm), lat = nrm.x * v.y - nrm.y * v.x;
        if (along <= 0.0) return vec3(0.0);
        float phi = atan(lat, along) / spread;          // -1..1 across the fan
        float k = exp(-phi * phi * 1.6) * exp(-along * 5.5) * smoothstep(0.0, 0.05, along);
        return spectral(clamp(phi * 0.5 + 0.5, 0.0, 1.0)) * k;
      }
      void main(){
        vec2 p = (vUv - 0.5) * 4.4;                  // world units around the medal centre
        float r = length(p);
        float out_ = smoothstep(0.80, 0.86, r);
        // FRONT DOOR 3: no rays — a quiet backlight. A soft rim halo and a
        // broad cool pool behind the disc, warmed a touch toward the key side.
        float halo = exp(-pow((r - 0.86) * 9.0, 2.0)) * 0.08 + exp(-(r - 0.82) * 4.0) * 0.035;
        float pool = exp(-r * r * 1.1) * 0.035;
        float warm = smoothstep(-1.0, 1.0, (p.x * 0.6 + p.y) / max(r, 0.001));
        vec3 tint = mix(vec3(0.62, 0.64, 0.86), vec3(1.0, 0.88, 0.74), warm * 0.45);
        vec3 col = tint * (halo * (1.0 + uBoost * 1.6) * out_ + pool * (1.0 + uBoost * 2.0));
        col *= uIgnite * uFade * smoothstep(2.2, 1.4, r);
        if (uPrism > 0.002) {
          vec3 fan = prismFan(p, uPrismAng, 0.16) + 0.45 * prismFan(p, uPrismAng + 3.3, 0.13);
          // the same split along the rim itself: a short dispersed glint arc
          float a = atan(p.y, p.x), da = atan(sin(a - uPrismAng), cos(a - uPrismAng));
          vec3 rimSpec = spectral(clamp(da / 0.5 + 0.5, 0.0, 1.0)) * exp(-pow((r - 0.835) * 34.0, 2.0)) * exp(-da * da * 7.0);
          col += (fan * 0.55 + rimSpec * 1.3) * uPrism * uFade * out_;
        }
        if (uHalo > 0.002) {
          // spectral halo: the rim light dispersed all the way round
          vec3 halo = vec3(exp(-pow((r - 0.875) * 22.0, 2.0)), exp(-pow((r - 0.86) * 22.0, 2.0)), exp(-pow((r - 0.845) * 22.0, 2.0)));
          col += halo * uHalo * 1.8 * uFade;
        }
        // the plunge hands its light over to the destination's colour
        col = mix(col, dot(col, vec3(0.3333)) * uDest * 1.15, uDestK);
        gl_FragColor = vec4(col, 1.0);
      }
    `,
  }));
  mesh.position.z = -0.12;
  return { mesh, uniforms };
}

// rim glints: a few four-point stars that flash on the medal's edge as if it
// catches a light, each at a fresh rim angle every cycle
function makeGlints(n) {
  const pos = new Float32Array(n * 3);
  const seed = new Float32Array(n);
  for (let i = 0; i < n; i++) seed[i] = (i + 0.37) / n;
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  geo.setAttribute('aSeed', new THREE.BufferAttribute(seed, 1));
  const uniforms = { uTime: { value: 0 }, uFade: { value: 1 }, uPx: { value: 60 } };
  const mat = new THREE.ShaderMaterial({
    uniforms, transparent: true, depthWrite: false, depthTest: false, blending: THREE.AdditiveBlending,
    vertexShader: /* glsl */`
      attribute float aSeed; varying float vA; uniform float uTime, uPx;
      float h(float x){ return fract(sin(x * 78.233) * 43758.5453); }
      void main(){
        float period = 3.1 + aSeed * 2.3;
        float cyc = floor(uTime / period + aSeed * 3.0);
        float ph = fract(uTime / period + aSeed * 3.0);
        float ang = h(cyc + aSeed * 17.0) * 6.28318;
        vA = pow(max(0.0, sin(ph * 3.14159)), 14.0);
        vec3 p = vec3(cos(ang) * 0.79, sin(ang) * 0.79, 0.07);
        gl_Position = projectionMatrix * modelViewMatrix * vec4(p, 1.0);
        gl_PointSize = uPx * (0.6 + 0.4 * h(cyc * 3.1 + aSeed));
      }
    `,
    fragmentShader: /* glsl */`
      precision highp float; varying float vA; uniform float uFade;
      void main(){
        vec2 c = (gl_PointCoord - 0.5) * 2.0;
        float core = exp(-length(c) * 7.0);
        vec3 col = vec3(1.0, 0.95, 0.9) * core * 1.6 * vA * uFade;
        gl_FragColor = vec4(col, 1.0);
      }
    `,
  });
  const pts = new THREE.Points(geo, mat);
  pts.frustumCulled = false;
  pts.renderOrder = 3;
  return { pts, uniforms };
}

function drawTextureSource(g, image, size) {
  const sourceSize = Math.min(Number(image.width) || size, Number(image.height) || size);
  const crop = sourceSize * CREST_CROP_RATIO;
  const sx = ((Number(image.width) || sourceSize) - crop) / 2;
  const sy = ((Number(image.height) || sourceSize) - crop) / 2;
  const fromBitmap = typeof ImageBitmap !== 'undefined' && image instanceof ImageBitmap;
  if (fromBitmap) { g.translate(0, size); g.scale(1, -1); }
  g.drawImage(image, sx, sy, crop, crop, 0, 0, size, size);
}

function bumpFromTexture(tex) {
  const image = tex && tex.image;
  if (!image) return null;
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = 256;
  const g = canvas.getContext('2d', { willReadFrequently: true });
  try { drawTextureSource(g, image, 256); } catch { return null; }
  const data = g.getImageData(0, 0, 256, 256);
  for (let i = 0; i < data.data.length; i += 4) {
    const y = data.data[i] * 0.24 + data.data[i + 1] * 0.62 + data.data[i + 2] * 0.14;
    const h = Math.max(0, Math.min(255, (y - 70) * 1.42));
    data.data[i] = data.data[i + 1] = data.data[i + 2] = h;
    data.data[i + 3] = 255;
  }
  g.putImageData(data, 0, 0);
  const bump = new THREE.CanvasTexture(canvas);
  bump.colorSpace = THREE.NoColorSpace;
  bump.minFilter = THREE.LinearMipmapLinearFilter;
  bump.magFilter = THREE.LinearFilter;
  return bump;
}

function goldFromTexture(tex) {
  const image = tex && tex.image;
  if (!image) return null;
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = 512;
  const g = canvas.getContext('2d', { willReadFrequently: true });
  try { drawTextureSource(g, image, 512); } catch { return null; }
  const data = g.getImageData(0, 0, 512, 512);
  for (let i = 0; i < data.data.length; i += 4) {
    const lum = (data.data[i] * 0.24 + data.data[i + 1] * 0.62 + data.data[i + 2] * 0.14) / 255;
    const shaped = Math.pow(lum, 0.82);
    const c = shaped * shaped * (3 - 2 * shaped);          // S-curve: chrome contrast
    data.data[i] = Math.min(255, 24 + c * 224);       // titanium: neutral highs,
    data.data[i + 1] = Math.min(255, 25 + c * 225);   // a cool blue-grey cast in
    data.data[i + 2] = Math.min(255, 36 + c * 219);   // the shadows only
  }
  g.putImageData(data, 0, 0);
  const gold = new THREE.CanvasTexture(canvas);
  gold.colorSpace = THREE.SRGBColorSpace;
  gold.minFilter = THREE.LinearMipmapLinearFilter;
  gold.magFilter = THREE.LinearFilter;
  return gold;
}

export function createEntryFX(ctx) {
  const group = new THREE.Group();
  group.visible = false;
  group.renderOrder = 20;
  const medalRoot = new THREE.Group();
  const maskRoot = new THREE.Group();
  const rng = mulberry32((ctx.seed || 7) * 1907 + 41);
  const motionRng = mulberry32((ctx.seed || 7) * 7919 + 83);
  const flipSchedule = [];
  let flipStart = 20 + motionRng() * 20;
  for (let i = 0; i < 64; i++) {
    const duration = 5 + motionRng() * 2;
    const midpoint = flipStart + duration / 2;
    const midpointVelocity = noiseYawDeg(midpoint + 0.01) - noiseYawDeg(midpoint - 0.01);
    flipSchedule.push({ start: flipStart, duration, direction: midpointVelocity > 0 ? -1 : 1 });
    flipStart += 20 + motionRng() * 20;
  }
  const coarse = matchMedia('(pointer: coarse)').matches || Math.min(innerWidth, innerHeight) < 700;
  let tier = ctx.perfMode() ? 'low' : coarse ? 'mid' : 'high';
  const segments = tier === 'high' ? 96 : tier === 'mid' ? 64 : 40;
  const reeds = tier === 'high' ? 84 : tier === 'mid' ? 56 : 32;
  const allocatedParticleCount = coarse ? 54 : 92;

  const gold = new THREE.MeshPhysicalMaterial({
    color: 0xd2d4dc, metalness: 0.7, roughness: 0.26, emissive: 0x262838, emissiveIntensity: 0.12,
    clearcoat: 0.40, clearcoatRoughness: 0.14, envMapIntensity: 0, transparent: true,
    iridescence: 1, iridescenceIOR: 1.5, iridescenceThicknessRange: [220, 640],
  });
  const side = new THREE.Mesh(medalBodyGeometry(segments), gold);
  medalRoot.add(side);

  const backPlate = new THREE.Mesh(new THREE.CircleGeometry(0.724, segments), new THREE.MeshPhysicalMaterial({
    color: 0x8e909c, metalness: 0.48, roughness: 0.34, emissive: 0x24263a,
    emissiveIntensity: 0.58, envMapIntensity: 0, transparent: true,
  }));
  backPlate.rotation.y = Math.PI;
  backPlate.position.z = -MEDAL_HALF_DEPTH;
  medalRoot.add(backPlate);

  const crestMat = new THREE.MeshPhysicalMaterial({
    color: 0xe4e6ee, metalness: 0.62, roughness: 0.24, bumpScale: 0.14,
    emissive: 0x2c3046, emissiveIntensity: 0.10,
    clearcoat: 0.36, clearcoatRoughness: 0.16, envMapIntensity: 0, transparent: true,
    iridescence: 0.85, iridescenceIOR: 1.45, iridescenceThicknessRange: [260, 700],
  });
  const faceGeometry = new THREE.CircleGeometry(0.744, segments);
  const face = new THREE.Mesh(faceGeometry, crestMat);
  face.position.z = MEDAL_HALF_DEPTH + 0.006;
  medalRoot.add(face);
  const backFace = new THREE.Mesh(faceGeometry, crestMat.clone());
  backFace.rotation.y = Math.PI;
  backFace.position.z = -MEDAL_HALF_DEPTH - 0.006;
  medalRoot.add(backFace);
  const flatFace = new THREE.Mesh(faceGeometry, crestMat.clone());
  flatFace.position.copy(face.position);
  flatFace.visible = false;
  medalRoot.add(flatFace);
  const goldFace = new THREE.Mesh(faceGeometry, crestMat.clone());
  goldFace.position.copy(face.position);
  goldFace.visible = false;
  medalRoot.add(goldFace);
  const orientationFace = new THREE.Mesh(faceGeometry, new THREE.MeshBasicMaterial({
    color: 0xffffff, transparent: true, toneMapped: false,
  }));
  orientationFace.position.copy(face.position);
  orientationFace.visible = false;
  medalRoot.add(orientationFace);
  const orientationGoldFace = new THREE.Mesh(faceGeometry, new THREE.MeshBasicMaterial({
    color: 0xe0a13a, transparent: true, toneMapped: false,
  }));
  orientationGoldFace.position.copy(face.position);
  orientationGoldFace.visible = false;
  medalRoot.add(orientationGoldFace);
  const backFlatFace = new THREE.Mesh(faceGeometry, crestMat.clone());
  backFlatFace.rotation.y = Math.PI;
  backFlatFace.position.copy(backFace.position);
  backFlatFace.visible = false;
  medalRoot.add(backFlatFace);
  const backOrientationFace = new THREE.Mesh(faceGeometry, new THREE.MeshBasicMaterial({
    color: 0xffffff, transparent: true, toneMapped: false,
  }));
  backOrientationFace.rotation.y = Math.PI;
  backOrientationFace.position.copy(backFace.position);
  backOrientationFace.visible = false;
  medalRoot.add(backOrientationFace);

  const rimMaterial = new THREE.MeshPhysicalMaterial({
    color: 0xeceef4, metalness: 0.74, roughness: 0.2,
    emissive: 0x30344c, emissiveIntensity: 0.35,
    clearcoat: 0.40, clearcoatRoughness: 0.10, envMapIntensity: 0, transparent: true,
    iridescence: 1, iridescenceIOR: 1.6, iridescenceThicknessRange: [180, 560],
  });
  const frontRim = new THREE.Mesh(new THREE.TorusGeometry(0.777, 0.031, 12, segments), rimMaterial);
  frontRim.position.z = MEDAL_HALF_DEPTH + 0.002;
  medalRoot.add(frontRim);
  const backRim = new THREE.Mesh(new THREE.TorusGeometry(0.777, 0.031, 12, segments), rimMaterial);
  backRim.rotation.y = Math.PI;
  backRim.position.z = -MEDAL_HALF_DEPTH - 0.002;
  medalRoot.add(backRim);

  const reedMesh = new THREE.InstancedMesh(new THREE.BoxGeometry(0.016, 0.03, MEDAL_HALF_DEPTH * 1.5),
    new THREE.MeshStandardMaterial({ color: 0xa2a4b0, metalness: 0.68, roughness: 0.40,
      emissive: 0x26283a, emissiveIntensity: 0.62, envMapIntensity: 0, transparent: true }), reeds);
  const m = new THREE.Matrix4();
  for (let i = 0; i < reeds; i++) {
    const a = i / reeds * Math.PI * 2;
    m.compose(new THREE.Vector3(Math.cos(a) * 0.798, Math.sin(a) * 0.798, 0),
      new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 0, 1), a), new THREE.Vector3(1, 1, 1));
    reedMesh.setMatrixAt(i, m);
  }
  medalRoot.add(reedMesh);

  // the light sweep is now a spectral caustic that follows the relief
  const sheen = makeCaustic(0.738, MEDAL_HALF_DEPTH + 0.024);
  medalRoot.add(sheen.mesh);
  group.add(medalRoot);

  const medalKey = new THREE.PointLight(0xdfe6ff, 3.2, 16, 1.65);
  medalKey.position.set(1.8, 2.5, 4.2);
  group.add(medalKey);
  const medalFill = new THREE.PointLight(0xc4cbe0, 1.1, 12, 1.8);
  medalFill.position.set(-2.8, -1.5, 2.6);
  group.add(medalFill);

  const mask = new THREE.Mesh(new THREE.CircleGeometry(0.82, segments),
    new THREE.MeshBasicMaterial({ color: 0xffffff, toneMapped: false }));
  mask.position.z = MEDAL_HALF_DEPTH + 0.018;
  maskRoot.add(mask);
  maskRoot.visible = false;
  group.add(maskRoot);

  const stage = makeStage();
  group.add(stage.mesh);
  const shock = makeShockwave();
  const particles = makeParticles(rng, allocatedParticleCount);
  particles.points.position.z = 0.02;
  group.add(particles.points);

  // V2.1: chrome environment, eclipse corona, orbit rings, rim glints
  // the env bake (PMREM passes + their shader compiles) is deferred until
  // the medal has landed and idled a beat: it must never delay the <300ms
  // reveal or the loader handoff frame
  let envDone = false;
  function applyEnv() {
    envDone = true;
    const env = makePrismEnv(ctx.renderer);
    if (!env) return;
    medalRoot.traverse((o) => {
      const m = o.material;
      if (!m || !m.isMeshStandardMaterial) return;
      m.envMap = env; m.envMapIntensity = 1.35;
      m.metalness = Math.max(m.metalness, 0.78);
      m.emissiveIntensity *= 0.62;            // contrast comes from reflections now
      m.needsUpdate = true;
    });
  }
  const auraRoot = new THREE.Group();
  const corona = makeCorona();
  auraRoot.add(corona.mesh);
  shock.mesh.renderOrder = 4;
  auraRoot.add(shock.mesh);                 // follows the medal's position and scale
  // FRONT DOOR 3: the orbit rings are retired (one object, no halo rings)
  const orbits = [];
  for (const o of orbits) auraRoot.add(o.mesh);
  group.add(auraRoot);
  const glints = makeGlints(coarse ? 4 : 6);
  medalRoot.add(glints.pts);

  // FRONT DOOR 6 spell layer. Sizes follow the initial device tier; a later
  // drop to the low tier (perfMode) hides the plasma and the post pass.
  const fxRng = mulberry32((ctx.seed || 7) * 4099 + 211);
  // The spell layer is tiered by the actual GPU (Jack: phone silicon gets
  // phone mode), not the chassis: mobile GPUs run 'mid' (3-octave fire, 7-tap
  // zoom, fewer sparks), software GL runs 'low' (no fire, no post pass), and
  // the adaptive perf tier (perfMode) drops to 'low' at runtime.
  const spellBase = (() => {
    let gpu = '';
    try {
      const gl = ctx.renderer?.getContext();
      const ext = gl?.getExtension('WEBGL_debug_renderer_info');
      gpu = String(gl?.getParameter(ext ? ext.UNMASKED_RENDERER_WEBGL : gl.RENDERER) || '');
    } catch { /* fingerprint-resistant browsers */ }
    if (/SwiftShader|llvmpipe|Software|Microsoft Basic Render/i.test(gpu)) return 'low';
    if (/Adreno|Mali|PowerVR|Xclipse|Immortalis|Apple GPU/i.test(gpu)) return 'mid';
    if (!gpu && navigator.userAgentData?.mobile) return 'mid';
    return tier;
  })();
  const spellTier = () => (ctx.perfMode() ? 'low' : spellBase);
  const strikeBolts = makeLightning({ slots: 6, width: 0.075, renderOrder: 6, core: [5.2, 5.0, 6.0], glow: [0.36, 0.48, 1.5] });
  auraRoot.add(strikeBolts.mesh);
  const faceArcs = makeLightning({ slots: 6, width: 0.045, renderOrder: 6,
    core: [5.2, 5.0, 6.0], glow: [0.36, 0.48, 1.5] });
  faceArcs.mesh.position.z = MEDAL_HALF_DEPTH + 0.03;
  medalRoot.add(faceArcs.mesh);
  const plasma = makePlasma(spellBase === 'high' ? 4 : 3);
  auraRoot.add(plasma.mesh);
  const sparks = makeSparks(fxRng, spellBase === 'high' ? 220 : spellBase === 'mid' ? 150 : 80, STRIKE_SITES);
  auraRoot.add(sparks.mesh);
  const lensPass = spellBase === 'low' ? null : makeLensPass(spellBase === 'high' ? 10 : 7);
  // the spell programs stay out of main.js's pre-reveal shader warm-up (it is
  // time-boxed and must keep the <300ms reveal); they compile in parallel once
  // the medal has landed and idled, well before the first arc or the tap
  for (const m of [strikeBolts.mesh, faceArcs.mesh, plasma.mesh, sparks.mesh]) m.visible = false;
  let spellWarm = false;
  function warmSpell() {
    spellWarm = true;
    if (!ctx.renderer?.compileAsync) return;
    const tmp = new THREE.Scene();
    for (const m of [strikeBolts.mesh, faceArcs.mesh, plasma.mesh, sparks.mesh])
      tmp.add(new THREE.Mesh(m.geometry, m.material));
    if (lensPass) tmp.add(new THREE.Mesh(new THREE.PlaneGeometry(2, 2), lensPass.material));
    for (const o of tmp.children) o.frustumCulled = false;
    // compile for the target they really draw into (the composer's HDR
    // buffer): a screen-target compile has a different output colour space,
    // so every spell program would be rebuilt on the first exit frames
    const prev = ctx.renderer.getRenderTarget();
    const rt = ctx.compileTarget?.();
    if (rt) ctx.renderer.setRenderTarget(rt);
    ctx.renderer.compileAsync(tmp, ctx.camera).catch(() => {});
    ctx.renderer.setRenderTarget(prev);
  }
  const beats = { strike: false, climax: false, plunge: false };
  let nextIdleArc = 2.4;
  let arcClock = 0;
  const rimPoint = (a, r = 0.81) => [Math.cos(a) * r, Math.sin(a) * r];
  // an arc that crawls across the relief between two rim points, re-forming
  // its path every ~50ms while it lives
  function faceArc(now, inten, life) {
    const a0 = fxRng() * Math.PI * 2, a1 = a0 + Math.PI * (0.55 + fxRng() * 0.8) * (fxRng() < 0.5 ? -1 : 1);
    const p0 = rimPoint(a0, 0.72), p1 = rimPoint(a1, 0.72);
    faceArcs.spawn(now, () => boltLines(fxRng, p0, p1, { levels: 5, rough: 0.24, branches: 2, inten: 1, fadeIn: 0, taperEnd: 0.8 }),
      { life, regen: 0.05, inten });
  }
  function pressStrike(now) {
    const origins = [2.05, 0.95, 2.6];
    STRIKE_SITES.forEach((site, i) => {
      const o = rimPoint(origins[i] + (fxRng() - 0.5) * 0.2, 3.6 + fxRng() * 0.6);
      const hit = rimPoint(site, 0.8);
      const lines = boltLines(fxRng, o, hit, { levels: 6, rough: 0.17, branches: 3, inten: 1, fadeIn: 0.25, taperEnd: 0.75 });
      strikeBolts.spawn(now + i * 0.012, () => lines, { life: 0.42, inten: 1.0 - i * 0.12,
        strokes: [[0, 1], [0.07, 0.72], [0.15, 0.55]] });
    });
  }
  function discharge(now) {
    for (let i = 0; i < 6; i++) {
      const a = i / 6 * Math.PI * 2 + 0.3 + (fxRng() - 0.5) * 0.5;
      const from = rimPoint(a, 0.82);
      const to = rimPoint(a + (fxRng() - 0.5) * 0.5, 2.3 + fxRng() * 1.1);
      const lines = boltLines(fxRng, from, to, { levels: 6, rough: 0.2, branches: 3, inten: 1, fadeIn: 0, taperEnd: 0.15 });
      strikeBolts.spawn(now + i * 0.01, () => lines, { life: 0.5, inten: 0.85,
        strokes: [[0, 1], [0.06, 0.7], [0.13, 0.5]] });
    }
  }
  let shake = 0;

  const metalMats = [];
  medalRoot.traverse((o) => { if (o.material?.transparent && o.material !== sheen.mesh.material && !metalMats.includes(o.material)) metalMats.push(o.material); });
  let lastMetalA = 1;
  const events = [];
  let phase = 'hidden';
  let elapsed = 0;
  let phaseStartedPerf = 0;
  let fixedTime = null;
  let entryOnPerf = 0;
  let visibleAtMs = -1;
  let landed = false;
  let debugMode = 'final';
  let exitResolve = null;
  let fitY = 0.34;
  let fitScale = 1;
  let fittedDiscPx = 0;
  let stageHeld = true;
  let crestReady = false;
  let orientation = { yawDeg: 0, pitchDeg: 2, rollDeg: 0, totalYawDeg: 0 };
  let backFacing = false;
  const settled = () => ctx.reduced;

  function idlePose(time) {
    const yawDeg = noiseYawDeg(time);
    const pitchDeg = 2 + 5 * Math.sin(time * Math.PI * 2 / 8.7)
      + 3 * Math.sin(time * Math.PI * 2 / 12.1);
    const rollDeg = 3 * Math.sin(time * Math.PI * 2 / 7.7)
      + 2 * Math.sin(time * Math.PI * 2 / 12.3);
    let flipAngle = 0;
    for (const flip of flipSchedule) {
      if (time < flip.start) break;
      const progress = clamp01((time - flip.start) / flip.duration);
      flipAngle += flip.direction * flipProgress(progress) * Math.PI * 2;
      if (progress < 1) break;
    }
    return { yawDeg, pitchDeg, rollDeg, flipAngle };
  }

  function motionAt(time) {
    const pose = idlePose(Math.max(0, Number(time) || 0));
    const totalYawDeg = pose.yawDeg + THREE.MathUtils.radToDeg(pose.flipAngle);
    return { yawDeg: +pose.yawDeg.toFixed(3), pitchDeg: +pose.pitchDeg.toFixed(3),
      rollDeg: +pose.rollDeg.toFixed(3), totalYawDeg: +totalYawDeg.toFixed(3),
      backFacing: Math.cos(THREE.MathUtils.degToRad(totalYawDeg)) < 0 };
  }

  function setCrest(tex) {
    if (!tex) return;
    crestMat.map = goldFromTexture(tex);
    crestMat.emissiveMap = crestMat.map;
    crestMat.bumpMap = bumpFromTexture(tex);
    crestMat.needsUpdate = true;
    // QA's flat face shares the exact albedo/emission with the relief face;
    // only bump is absent, so their luminance delta cannot be satisfied by a
    // full-disc tint. The unlit face isolates bitmap orientation from lights.
    flatFace.material.map = crestMat.map;
    flatFace.material.emissiveMap = crestMat.map;
    flatFace.material.bumpMap = null;
    flatFace.material.needsUpdate = true;
    orientationFace.material.map = crestMat.map;
    orientationFace.material.needsUpdate = true;
    for (const material of [backFace.material, backFlatFace.material]) {
      material.map = crestMat.map;
      material.emissiveMap = crestMat.map;
      material.bumpMap = material === backFlatFace.material ? null : crestMat.bumpMap;
      material.needsUpdate = true;
    }
    backOrientationFace.material.map = crestMat.map;
    backOrientationFace.material.needsUpdate = true;
    sheen.uniforms.tBump.value = crestMat.bumpMap;
    sheen.uniforms.uHasBump.value = crestMat.bumpMap ? 1 : 0;
    crestReady = true;
  }

  function show(onPerf = performance.now()) {
    refit();
    entryOnPerf = onPerf;
    visibleAtMs = Math.max(0, performance.now() - entryOnPerf);
    elapsed = 0;
    phaseStartedPerf = performance.now();
    phase = settled() ? 'idle' : 'arrival';
    landed = settled();
    group.visible = true;
    if (landed) {
      events.push({ kind: 'land', atMs: Math.round(performance.now() - entryOnPerf), reduced: true });
      ctx.onLand?.();
    }
    applyMode('final');
  }

  function land() {
    if (landed) return;
    landed = true;
    phase = 'idle';
    events.push({ kind: 'land', atMs: Math.round(performance.now() - entryOnPerf) });
    shock.uniforms.uT.value = 0;
    shock.mesh.visible = true;
    ctx.onLand?.();
  }

  function startExit() {
    if (phase === 'exit' || phase === 'done') return Promise.resolve();
    phase = 'exit';
    elapsed = 0;
    phaseStartedPerf = performance.now();
    events.push({ kind: 'exit-start', atMs: Math.round(performance.now() - entryOnPerf) });
    beats.strike = beats.climax = beats.plunge = false;
    strikeAt = climaxAt = null; arcClock = 0;
    sparks.uniforms.uBirth0.value = sparks.uniforms.uBirth1.value = 99;
    faceArcs.clear();
    if (!settled()) { shock.uniforms.uT.value = 0; shock.mesh.visible = true; }   // the thud rings the stage
    return new Promise((resolve) => { exitResolve = resolve; });
  }

  // a tap mid-sequence: jump to the reveal frame; the tail of the plunge plays out
  function revealSeconds() { return settled() ? CALM_REVEAL : EXIT_REVEAL; }
  function skipExit() {
    const reveal = revealSeconds();
    if (phase !== 'exit' || elapsed >= reveal || fixedTime != null) return false;
    phaseStartedPerf = performance.now() - reveal * 1000;
    beats.strike = beats.climax = beats.plunge = true;      // never fire a skipped beat late
    strikeBolts.clear(); faceArcs.clear();
    events.push({ kind: 'exit-skip', atMs: Math.round(performance.now() - entryOnPerf) });
    return true;
  }

  function finishExit() {
    if (phase === 'done') return;
    phase = 'done';
    group.visible = false;
    if (lensPass) lensPass.enabled = false;
    strikeBolts.clear(); faceArcs.clear();
    events.push({ kind: 'exit-end', atMs: Math.round(performance.now() - entryOnPerf) });
    const resolve = exitResolve; exitResolve = null; resolve?.();
  }

  function applyMode(mode) {
    debugMode = mode;
    const effects = !settled();
    group.visible = mode !== 'hidden' && phase !== 'hidden' && phase !== 'done';
    medalRoot.visible = ['final', 'edge', 'side', 'face', 'face-flat', 'face-gold', 'face-unlit',
      'face-unlit-gold', 'face-flat-front', 'face-front', 'face-unlit-back',
      'face-flat-back', 'face-back'].includes(mode);
    maskRoot.visible = mode === 'medallion';
    side.visible = mode === 'final' || mode === 'edge' || mode === 'side';
    backPlate.visible = mode === 'edge' || mode === 'side';
    frontRim.visible = mode === 'final' || mode === 'edge';
    backRim.visible = mode === 'final' || mode === 'edge';
    reedMesh.visible = tier !== 'low' && (mode === 'final' || mode === 'edge' || mode === 'side');
    face.visible = mode === 'final' || mode === 'face' || mode === 'face-front';
    flatFace.visible = mode === 'face-flat' || mode === 'face-flat-front';
    goldFace.visible = mode === 'face-gold';
    orientationFace.visible = mode === 'face-unlit';
    orientationGoldFace.visible = mode === 'face-unlit-gold';
    backFace.visible = mode === 'final' || mode === 'face-back';
    backFlatFace.visible = mode === 'face-flat-back';
    backOrientationFace.visible = mode === 'face-unlit-back';
    sheen.mesh.visible = mode === 'final';
    stage.mesh.visible = mode === 'final' && stageHeld;
    stage.uniforms.uAurora.value = effects ? 1 : 0;
    shock.mesh.visible = effects && mode === 'final' && shock.uniforms.uT.value < 1;
    particles.points.visible = effects && (mode === 'final' || mode === 'particles');
    corona.mesh.visible = effects && mode === 'final';
    for (const o of orbits) o.mesh.visible = effects && mode === 'final';
    glints.pts.visible = effects && mode === 'final';
    strikeBolts.mesh.visible = effects && mode === 'final' && strikeBolts.active;
    faceArcs.mesh.visible = effects && mode === 'final' && faceArcs.active;
    plasma.mesh.visible = effects && mode === 'final' && spellTier() !== 'low' && plasma.uniforms.uAmt.value > 0.003;
    sparks.mesh.visible = effects && mode === 'final' && phase === 'exit';
    if (['face-unlit', 'face-unlit-gold', 'face-flat-front', 'face-front'].includes(mode))
      medalRoot.rotation.set(0, 0, 0);
    if (['face-unlit-back', 'face-flat-back', 'face-back'].includes(mode))
      medalRoot.rotation.set(0, Math.PI, 0);
    syncMask();
  }

  function syncMask() {
    maskRoot.position.copy(medalRoot.position);
    maskRoot.quaternion.copy(medalRoot.quaternion);
    maskRoot.scale.copy(medalRoot.scale);
    auraRoot.position.copy(medalRoot.position);
    auraRoot.scale.copy(medalRoot.scale);
  }

  const _wp = new THREE.Vector3(), _dest = new THREE.Vector3(1, 0.8, 0.55);
  let strikeAt = null, climaxAt = null, rimHeat0 = null;
  const rimEmissive0 = rimMaterial.emissive.clone();
  const rimEmissiveHot = new THREE.Color(0.55, 0.74, 1.0);

  function update(now, dt, pointer = { x: 0, y: 0 }) {
    if (phase === 'hidden' || phase === 'done') return;
    refit();
    if (fixedTime == null) elapsed = Math.max(0, (performance.now() - phaseStartedPerf) / 1000);
    else elapsed = fixedTime;
    const motion = settled() ? 0 : 1;
    let scale = 1, ySpin = 0, xTumble = ARRIVAL_PITCH, roll = 0, z = 0, fade = 1, burst = 0, flare = 0;
    let bob = 0, ignite = 1, boost = 0, orbitAmp = 1, orbitScale = 1, glow = 0;
    // spell layer
    let plasmaAmt = 0, heat = 0, prism = 0, halo = 0, zoom = 0, flash = 0, caustic = 0.32, rimHeat = 0;
    let sweep = -1.4 + 2.8 * ((now * 0.085) % 1);
    let destK = 0;
    const fxNow = phase === 'exit' ? elapsed : now;

    if (phase === 'arrival') {
      stageHeld = true;
      const k = outQuint(elapsed / ARRIVAL_END);
      scale = 0.86 + 0.14 * k;
      bob = -0.16 * (1 - k) + Math.sin(k * Math.PI) * 0.035;
      ySpin = (1 - k) * -0.12;
      xTumble = ARRIVAL_PITCH + (1 - k) * 0.025;
      ignite = k; orbitAmp = k * k; orbitScale = 1.6 - 0.6 * k;
      prism = 0.2 * k * k;
      if (elapsed >= ARRIVAL_END) land();
    } else if (phase === 'idle') {
      stageHeld = true;
      const idle = Math.max(0, elapsed - ARRIVAL_END);
      const pose = idlePose(idle);
      ySpin = motion * (THREE.MathUtils.degToRad(pose.yawDeg) + pose.flipAngle);
      xTumble = motion * THREE.MathUtils.degToRad(pose.pitchDeg);
      roll = motion * THREE.MathUtils.degToRad(pose.rollDeg);
      bob = motion * Math.sin(idle * Math.PI * 2 / 5.8) * 0.026;
      burst = Math.max(0, 1 - idle / 0.72);
      boost = 0.35 * burst;                    // the land flashes the corona
      // alive at rest: the caustic crosses the relief, the prism fan breathes,
      // and now and then a faint arc crawls over the face
      prism = 0.16 + 0.06 * Math.sin(now * 0.7);
      if (motion && idle >= nextIdleArc) {
        faceArc(now, 0.3, 0.16 + fxRng() * 0.14);
        if (fxRng() < 0.4) faceArc(now + 0.05, 0.22, 0.12);
        nextIdleArc = idle + 2.2 + fxRng() * 2.8;
      }
      if (!envDone && idle > 0.9) applyEnv();
      if (!spellWarm && idle > 1.2) warmSpell();
    } else if (phase === 'exit' && settled()) {
      // reduced motion: no strike, spin or dive; the medal glows and dissolves
      const e = elapsed;
      scale = 1 + 0.03 * smooth(e / CALM_EXIT_END);
      fade = 1 - smooth((e - 0.1) / 0.5);
      glow = Math.sin(Math.min(e / 0.6, 1) * Math.PI) * 0.6;
      boost = glow; flare = 0.25 * glow;
      stageHeld = e < CALM_REVEAL;
      if (e >= CALM_EXIT_END) finishExit();
    } else if (phase === 'exit') {
      const e = elapsed;
      // PRESS: a short press-in under the strike
      const thud = e < THUD_END ? Math.sin((e / THUD_END) * Math.PI) : 0;
      // CHARGE: lift toward the lens, one slow turn that completes at the climax
      const g = smooth((e - GATHER_START) / (CLIMAX - GATHER_START));
      const pre = smooth((e - 0.3) / (CLIMAX - 0.3)) * (e < CLIMAX ? 1 : Math.exp(-(e - CLIMAX) / 0.25));
      const c = climaxAt != null && e >= CLIMAX ? Math.exp(-(e - CLIMAX) / 0.12) : 0;
      // PLUNGE: through the lens
      const q = smooth((e - PLUNGE_START) / (EXIT_END - PLUNGE_START));
      stageHeld = q < 0.2;
      ySpin = g * Math.PI * 2;
      xTumble = ARRIVAL_PITCH * (1 - g) + Math.sin(g * Math.PI) * 0.06 + q * 0.04;
      scale = (1 - thud * 0.065) * (1 + g * 0.1) * (1 + c * 0.05) + q * q * 3.0;
      bob = g * 0.05 * (1 - q) + pre * 0.004 * Math.sin(now * 61);   // held energy trembles
      z = g * 0.18 + q * 1.1 * (ctx.camera.position.z + 0.42);
      flare = Math.max(thud * 0.3, Math.pow(Math.sin(Math.min(q * 1.9, 1) * Math.PI), 1.4) * 0.95)
        + Math.sin(g * Math.PI) * 0.12 + c * 0.5;
      // cross-fade: the medal dissolves into light mid-plunge (title fades up
      // beneath it from EXIT_REVEAL), so the lens never fills with a flat close-up
      fade = 1 - smooth((q - 0.03) / 0.2);
      glow = Math.sin(Math.min(q / 0.55, 1) * Math.PI);
      burst = e < CLIMAX ? thud - 0.32 * pre : Math.max(c * 1.2, Math.sin(Math.min(q * 1.35, 1) * Math.PI) * 0.8);
      boost = Math.max(thud * 0.4, g * 0.3 + pre * 0.25 + c * 0.45 + Math.sin(Math.min(q * 2.2, 1) * Math.PI) * 0.5);

      if (!beats.strike) {
        beats.strike = true; strikeAt = e; pressStrike(e); shake = 1;
        sparks.uniforms.uBirth0.value = e;
        rimHeat0 = rimMaterial.emissiveIntensity;
      }
      if (!beats.climax && e >= CLIMAX) {
        beats.climax = true; climaxAt = e; discharge(e); shake = 1.3;
        sparks.uniforms.uBirth1.value = e;
        shock.uniforms.uT.value = 0; shock.mesh.visible = true;
      }
      if (e > 0.05 && e < CLIMAX) {
        arcClock += dt * (3 + 15 * pre);
        while (arcClock >= 1) { arcClock -= 1; faceArc(e, 0.55 + 0.45 * pre, 0.14 + fxRng() * 0.22); }
      }
      plasmaAmt = e < CLIMAX ? smooth((e - 0.2) / 0.7) * (0.5 + 0.5 * pre)
        : Math.max(0, 1 - smooth((e - CLIMAX) / 0.45)) + c * 0.25;
      plasmaAmt *= 1 - smooth(q / 0.3);
      heat = Math.min(1, plasmaAmt);
      rimHeat = Math.min(1, plasmaAmt + thud * 0.6);
      caustic = 0.32 + 0.6 * pre + 0.5 * c;
      sweep = -1.2 + 2.4 * ((e * 0.75) % 1);
      prism = 0.18 + 0.55 * pre + c * 1.0 + thud * 0.3;
      halo = c * 0.45 + thud * 0.15;
      zoom = smooth((e - PLUNGE_START) / 0.3) * (1 - smooth((e - 2.05) / 0.35));
      destK = smooth((e - 1.85) / 0.3);          // flows into the title's rift light
      flash = Math.exp(-Math.pow((e - 2.0) / 0.08, 2)) * 0.42 + Math.exp(-e / 0.04) * 0.08 + c * 0.05;
      if (elapsed >= EXIT_END) finishExit();
    }
    if (phase === 'done') return;

    // the metal itself dissolves during the plunge (it used to rely on flying
    // through the near plane, which left a flat grey close-up on screen)
    const metalA = phase === 'exit' ? fade : 1;
    if (metalA !== lastMetalA) {
      lastMetalA = metalA;
      for (const m of metalMats) m.opacity = metalA;
    }
    shake *= Math.exp(-dt / 0.09);
    const sx = motion * (fxRng() - 0.5) * shake * 0.05 * fitScale;
    const sy = motion * (fxRng() - 0.5) * shake * 0.05 * fitScale;
    medalRoot.position.set(pointer.x * 0.018 * motion + sx, fitY + bob - pointer.y * 0.012 * motion + sy, z);
    orientation = {
      yawDeg: +THREE.MathUtils.radToDeg(ySpin % (Math.PI * 2)).toFixed(3),
      pitchDeg: +THREE.MathUtils.radToDeg(xTumble).toFixed(3),
      rollDeg: +THREE.MathUtils.radToDeg(roll).toFixed(3),
      totalYawDeg: +THREE.MathUtils.radToDeg(ySpin).toFixed(3),
    };
    backFacing = Math.cos(ySpin) < 0;
    medalRoot.rotation.set(xTumble - pointer.y * 0.018 * motion,
      ySpin + pointer.x * 0.026 * motion, roll);
    if (['face-unlit', 'face-unlit-gold', 'face-flat-front', 'face-front'].includes(debugMode))
      medalRoot.rotation.set(0, 0, 0);
    if (['face-unlit-back', 'face-flat-back', 'face-back'].includes(debugMode))
      medalRoot.rotation.set(0, Math.PI, 0);
    medalRoot.scale.setScalar(fitScale * scale);
    syncMask();

    sheen.uniforms.uTime.value = ctx.reduced ? 1.6 : now;
    sheen.uniforms.uFade.value = fade;
    sheen.uniforms.uFlare.value = flare;
    sheen.uniforms.uAmt.value = motion ? caustic : 0.2;
    sheen.uniforms.uSweep.value = motion ? sweep : 0.1;
    stage.uniforms.uTime.value = ctx.reduced ? 3.2 : now;
    stage.uniforms.uFade.value = fade;
    particles.uniforms.uTime.value = ctx.reduced ? 2.0 : now;
    particles.uniforms.uBurst.value = burst;
    particles.uniforms.uFade.value = fade;
    particles.uniforms.uPointer.value.set(pointer.x, pointer.y);
    const tt = ctx.reduced ? 2.0 : now;
    corona.uniforms.uTime.value = tt;
    corona.uniforms.uIgnite.value = ignite;
    corona.uniforms.uBoost.value = boost;
    corona.uniforms.uFade.value = Math.max(fade, glow);
    corona.uniforms.uPrism.value = motion * prism;
    corona.uniforms.uPrismAng.value = 0.95 + ySpin * 0.5 + 0.25 * Math.sin(now * 0.21);
    corona.uniforms.uHalo.value = motion * halo;
    // destination light: the title's rift colour when it exposes one, else
    // its documented warm white-gold
    const dl = destK > 0 ? ctx.titleLight?.() : null;
    if (dl && Number.isFinite(dl.r)) {
      const m = Math.max(dl.r, dl.g, dl.b, 1e-3);
      _dest.set(dl.r / m, dl.g / m, dl.b / m);
    } else _dest.set(1, 0.8, 0.55);
    corona.uniforms.uDest.value.copy(_dest);
    corona.uniforms.uDestK.value = destK * 0.85;
    for (const o of orbits) {
      o.uniforms.uTime.value = tt; o.uniforms.uFade.value = fade; o.uniforms.uAmp.value = orbitAmp;
      o.mesh.scale.setScalar(orbitScale);
    }
    glints.uniforms.uTime.value = tt;
    glints.uniforms.uFade.value = fade * (phase === 'idle' ? 1 : 0);
    glints.uniforms.uPx.value = Math.max(24, fittedDiscPx * 0.2 * Math.min(devicePixelRatio || 1, 2));
    if (shock.mesh.visible) {
      shock.uniforms.uT.value += dt * 1.35;
      shock.uniforms.uFade.value = fade;
      if (shock.uniforms.uT.value >= 1) shock.mesh.visible = false;
    }

    // spell layer
    const flick = () => fxRng();
    strikeBolts.update(fxNow, flick);
    faceArcs.update(fxNow, flick);
    faceArcs.uniforms.uFade.value = fade;
    plasma.uniforms.uTime.value = now;
    plasma.uniforms.uAmt.value = motion * plasmaAmt;
    plasma.uniforms.uSquash.value = Math.max(Math.abs(Math.cos(ySpin)), 0.07);
    sparks.uniforms.uT.value = phase === 'exit' ? elapsed : -10;
    if (rimHeat0 != null) {
      rimMaterial.emissive.copy(rimEmissive0).lerp(rimEmissiveHot, rimHeat);
      rimMaterial.emissiveIntensity = rimHeat0 + rimHeat * 1.1;
    }
    if (lensPass) {
      const shock0 = strikeAt != null ? (elapsed - strikeAt) / 0.7 : -1;
      const shock1 = climaxAt != null ? (elapsed - climaxAt) / 0.85 : -1;
      const live = motion && phase === 'exit' && spellTier() !== 'low' && debugMode === 'final'
        && ((shock0 >= 0 && shock0 < 1) || (shock1 >= 0 && shock1 < 1) || heat > 0.01 || zoom > 0.001 || flash > 0.01);
      lensPass.enabled = !!live;
      if (live) {
        medalRoot.getWorldPosition(_wp);
        const dist = Math.max(0.2, ctx.camera.position.z - _wp.z);
        const visH = 2 * Math.tan(THREE.MathUtils.degToRad(ctx.camera.fov / 2)) * dist;
        const u = lensPass.uniforms;
        u.uR.value = Math.min(3, MEDAL_RADIUS * medalRoot.scale.x / visH);
        _wp.project(ctx.camera);
        u.uCenter.value.set(_wp.x * 0.5 + 0.5, _wp.y * 0.5 + 0.5);
        u.uAspect.value = innerWidth / Math.max(1, innerHeight);
        u.uShock0.value = shock0; u.uShock1.value = shock1;
        u.uHeat.value = heat; u.uZoom.value = zoom; u.uFlash.value = flash; u.uTime.value = now;
        u.uFlashCol.value.set(0.9, 0.9, 1.0).lerp(_dest, destK * 0.9);
      }
    }
    applyMode(debugMode);
  }

  function refit() {
    tier = ctx.perfMode() ? 'low'
      : (matchMedia('(pointer: coarse)').matches || Math.min(innerWidth, innerHeight) < 700) ? 'mid' : 'high';
    const rect = document.querySelector('#entry .en-crest')?.getBoundingClientRect();
    if (!rect || rect.width < 1 || innerHeight < 1) return;
    const visibleWorldHeight = 2 * Math.tan(THREE.MathUtils.degToRad(ctx.camera.fov / 2)) * ctx.camera.position.z;
    const worldPerPixel = visibleWorldHeight / innerHeight;
    fittedDiscPx = rect.width;
    fitScale = rect.width * worldPerPixel / MEDAL_DIAMETER;
    fitY = (innerHeight / 2 - (rect.top + rect.height / 2)) * worldPerPixel;
  }

  function setFixedTime(value) { fixedTime = Number.isFinite(value) ? Math.max(0, value) : null; }
  function markTap(perf = performance.now()) {
    events.push({ kind: 'tap', perf: +perf.toFixed(2), atMs: Math.round(perf - entryOnPerf) });
  }
  function debugState() {
    return {
      phase, tier, visible: group.visible, visibleAtMs: +visibleAtMs.toFixed(2),
      elapsed: +elapsed.toFixed(3), particleCount: particles.points.visible ? allocatedParticleCount : 0,
      settled: settled(), auroraVisible: stage.mesh.visible && stage.uniforms.uAurora.value > 0,
      shockVisible: shock.mesh.visible, debugMode, entryOnPerf: +entryOnPerf.toFixed(2),
      crestReady, orientation, backFacing,
      thicknessRatio: +THICKNESS_RATIO.toFixed(3),
      nextFlipAt: +flipSchedule[0].start.toFixed(3), flipDuration: +flipSchedule[0].duration.toFixed(3),
      exitDurationMs: (settled() ? CALM_EXIT_END : EXIT_END) * 1000, exitRevealMs: revealSeconds() * 1000, exitTurns: 1,
      spell: { strikes: strikeBolts.active, arcs: faceArcs.active, plasma: +plasma.uniforms.uAmt.value.toFixed(3),
        lens: !!lensPass?.enabled, sparks: sparks.count, tier: spellTier(), beats: { ...beats } },
      fittedDiscPx: +fittedDiscPx.toFixed(1),
      events: events.map((event) => ({ ...event })),
    };
  }

  return {
    group, show, update, refit, setCrest, startExit, skipExit, setFixedTime, markTap, motionAt,
    qaMode: applyMode, debugState, lensPass, revealSeconds,
    spellParts: { strikeBolts, faceArcs, plasma, sparks, lensPass, corona, shock, sheen, stage, particles },
    get active() { return phase !== 'hidden' && phase !== 'done'; },
  };
}
